<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { loadGoogleAnalytics, disableGoogleAnalytics, hasConsent } from '../utils/analytics'

const { t } = useI18n()
const showBanner = ref(false)

const acceptCookies = () => {
  localStorage.setItem('cookiesAccepted', 'true')
  showBanner.value = false
  // Charger Google Analytics après acceptation
  loadGoogleAnalytics()
}

const refuseCookies = () => {
  localStorage.setItem('cookiesAccepted', 'false')
  showBanner.value = false
  // Désactiver Google Analytics si déjà chargé
  disableGoogleAnalytics()
}

onMounted(() => {
  const cookiesAccepted = localStorage.getItem('cookiesAccepted')
  if (cookiesAccepted === null) {
    showBanner.value = true
  } else if (hasConsent()) {
    // Si le consentement a déjà été donné, charger Google Analytics
    loadGoogleAnalytics()
  }
})
</script>

<template>
  <div class="cookie-float">
    <Transition name="cookie-banner">
      <div
        v-if="showBanner"
        class="cookie-float__panel"
        role="dialog"
        :aria-label="t('legal.privacy.cookies')"
      >
        <div class="cookie-float__header">
          <div class="cookie-float__title-wrap">
            <span class="cookie-float__icon" aria-hidden="true">
              <i class="fas fa-cookie-bite" aria-hidden="true" />
            </span>
            <p class="cookie-float__title">
              {{ t('legal.privacy.cookies') }}
            </p>
          </div>
        </div>

        <p class="cookie-float__message">
          {{ t('cookies.message') }}
          <RouterLink to="/legal/privacy" class="cookie-float__link">
            {{ t('cookies.moreInfoPrivacy') }}
          </RouterLink>
        </p>

        <div class="cookie-float__actions">
          <button
            class="cookie-float__btn cookie-float__btn--secondary"
            type="button"
            @click="refuseCookies"
            :aria-label="t('cookies.refuseAria')"
          >
            <i class="fas fa-times" aria-hidden="true" />
            {{ t('cookies.refuse') }}
          </button>
          <button
            class="cookie-float__btn cookie-float__btn--primary"
            type="button"
            @click="acceptCookies"
            :aria-label="t('cookies.acceptAria')"
          >
            <i class="fas fa-check" aria-hidden="true" />
            {{ t('cookies.accept') }}
          </button>
        </div>
      </div>
    </Transition>
  </div>
</template>

<style scoped>
.cookie-float {
  position: fixed;
  left: 1rem;
  bottom: max(1rem, env(safe-area-inset-bottom));
  z-index: 110;
  pointer-events: none;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.cookie-float > * {
  pointer-events: auto;
}

.cookie-float__panel {
  width: min(22rem, calc(100vw - 2rem));
  background: rgba(255, 255, 255, 0.98);
  border-radius: 12px;
  box-shadow:
    0 4px 24px rgba(0, 0, 0, 0.12),
    0 0 0 1px rgba(var(--primary-color-rgb), 0.08);
  padding: 1rem 1rem 1.05rem;
  text-align: left;
  backdrop-filter: blur(10px);
}

.cookie-float__header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 0.5rem;
  margin-bottom: 0.6rem;
}

.cookie-float__title-wrap {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.cookie-float__title {
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
  line-height: 1.2;
}

.cookie-float__icon {
  width: 2rem;
  height: 2rem;
  border-radius: 10px;
  background: rgba(var(--primary-color-rgb), 0.08);
  color: var(--primary-color);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.cookie-float__icon i {
  font-size: 1rem;
}

.cookie-float__message {
  margin: 0;
  color: var(--text-secondary);
  font-size: 0.92rem;
  line-height: 1.35;
}

.cookie-float__actions {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 0.7rem;
  padding-top: 0.75rem;
  border-top: 1px solid rgba(var(--primary-color-rgb), 0.12);
  flex-wrap: nowrap;
}

.cookie-float__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.55rem 0.85rem;
  border-radius: 10px;
  font-size: 0.9rem;
  font-weight: 600;
  border: none;
  cursor: pointer;
  font-family: inherit;
  transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
  flex: 0 0 calc(50% - 0.375rem);
}

.cookie-float__btn--primary {
  background: var(--primary-color);
  color: #fff;
}

.cookie-float__btn--primary:hover {
  background: var(--primary-dark);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(var(--primary-color-rgb), 0.35);
}

.cookie-float__btn--secondary {
  background: transparent;
  color: var(--primary-color);
  border: 1px solid rgba(var(--primary-color-rgb), 0.25);
}

.cookie-float__btn--secondary:hover {
  background: rgba(var(--primary-color-rgb), 0.1);
}

.cookie-float__link {
  color: var(--primary-color);
  text-decoration: underline;
}
.cookie-float__link:hover {
  color: var(--primary-dark);
}

.cookie-banner-enter-active,
.cookie-banner-leave-active {
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
}

.cookie-banner-enter-from,
.cookie-banner-leave-to {
  opacity: 0;
  transform: translateY(8px) scale(0.98);
}

@media (max-width: 768px) {
  .cookie-float {
    left: 0.75rem;
    bottom: max(0.75rem, env(safe-area-inset-bottom));
  }

  .cookie-float__panel {
    padding: 0.95rem 0.95rem 1rem;
  }

  .cookie-float__actions {
    justify-content: center;
    gap: 0.6rem;
  }

  .cookie-float__btn {
    flex-basis: calc(50% - 0.3rem);
  }
}
</style> 